import {
  addExcludedId,
  addSelectedId,
  removeExcludedId,
  removeSelectedId,
  resetExcludedId,
  resetSelectedId,
  setIsNeedToSelectAll,
  setIsSelecting,
} from '@entities/common/IdsSelector/reducer';
import {
  excludedIdsHashSelector,
  isIdsSelectingHashSelector,
  isNeedToSelectAllHashSelector,
  selectedIdsHashSelector,
} from '@entities/common/IdsSelector/selectors';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@shared/hooks';

export const useIdsSelecting = (name: string) => {
  const nav = useNavigate();

  const isSelectingHash = useAppSelector(isIdsSelectingHashSelector);
  const isNeedToSelectAllHash = useAppSelector(isNeedToSelectAllHashSelector);
  const dispatch = useAppDispatch();

  const setIsSelectingFalse = useCallback(
    () => dispatch(setIsSelecting({ name, isSelecting: false })),
    [name, dispatch]
  );

  const setIsSelectingTrue = useCallback(
    () => dispatch(setIsSelecting({ name, isSelecting: true })),
    [name, dispatch]
  );

  const setIsNeedToSelectAllSelectingFalse = useCallback(() => {
    dispatch(setIsNeedToSelectAll({ name, isNeedToSelectAll: false }));
  }, [name, dispatch]);

  const setIsNeedToSelectAllSelectingTrue = useCallback(() => {
    dispatch(setIsNeedToSelectAll({ name, isNeedToSelectAll: true }));
  }, [name, dispatch]);

  const onBackOrReady = useCallback(() => {
    nav(-1);
    setTimeout(() => setIsSelectingFalse(), 0);
    setTimeout(() => setIsNeedToSelectAllSelectingFalse(), 0);
    // console.log('We should Redirect there');
  }, []);

  const isSelecting = isSelectingHash[name];
  const isNeedToSelectAll = isNeedToSelectAllHash[name];

  return {
    isSelecting,
    setIsSelectingTrue,
    setIsSelectingFalse,
    isNeedToSelectAll,
    setIsNeedToSelectAllSelectingFalse,
    setIsNeedToSelectAllSelectingTrue,
    onBackOrReady,
  };
};

export const useIdsSelect = (name: string) => {
  const dispatch = useAppDispatch();

  const selectedIdsHash = useAppSelector(selectedIdsHashSelector);
  const excludedIdsHash = useAppSelector(excludedIdsHashSelector);

  const selectedIds = selectedIdsHash[name];
  const excludedIds = excludedIdsHash[name];

  const doResetSelectedIds = useCallback(
    (newIds: number[]) => dispatch(resetSelectedId({ name, newIds })),
    [dispatch, name]
  );
  const doResetExcludedIds = useCallback(
    (newIds: number[]) => dispatch(resetExcludedId({ name, newIds })),
    [dispatch, name]
  );

  const doAddId = useCallback(
    (id: number) => dispatch(addSelectedId({ name, id })),
    [dispatch, name]
  );

  const doRemoveId = useCallback(
    (id: number) => dispatch(removeSelectedId({ name, id })),
    [dispatch, name]
  );

  const doAddExcludedId = useCallback(
    (id: number) => dispatch(addExcludedId({ name, id })),
    [dispatch, name]
  );

  const doRemoveExcludedId = useCallback(
    (id: number) => dispatch(removeExcludedId({ name, id })),
    [dispatch, name]
  );

  const isIdSelected = useCallback(
    (checkedId: number) => {
      let output = false;
      selectedIds && (output = selectedIds.some((id) => id === checkedId));
      return output;
    },
    [name, selectedIds]
  );

  const doToggleId = useCallback(
    (id: number) => (isIdSelected(id) ? doRemoveId(id) : doAddId(id)),
    [doRemoveId, doAddId, isIdSelected]
  );

  return {
    selectedIds,
    excludedIds,
    doAddId,
    doRemoveId,
    doToggleId,
    doResetSelectedIds,
    isIdSelected,
    doAddExcludedId,
    doRemoveExcludedId,
    doResetExcludedIds,
  };
};
